import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import styles from "./TutorTeachingPreferences.module.css";
import { adminAPI } from "../axiosInstance";
import {
  BOARD_LABELS,
  BOARD_OPTIONS,
  GRADE_LABELS,
  GRADE_OPTIONS,
  filterCategoriesForSelection,
  groupCategoriesBySubjectName,
  isSubjectGroupSelected,
  toggleSubjectGroupSelection,
} from "../tutorTeachingUtils";

const TutorTeachingPreferences = ({
  subjectData = [],
  selectedBoards = [],
  selectedGrades = [],
  selectedCategoryIds = [],
  studyGroupTypes = { oneOnOne: true, group: false },
  onBoardsChange,
  onGradesChange,
  onCategoryIdsChange,
  onStudyGroupTypesChange,
  onSubjectCatalogUpdated,
  allowAddMissingSubject = false,
  addedByUserId = "",
  addedByTutorId = "",
  addedByName = "",
  errors = {},
}) => {
  const [subjectSearch, setSubjectSearch] = useState("");
  const [showAddSubject, setShowAddSubject] = useState(false);
  const [newSubjectName, setNewSubjectName] = useState("");
  const [addingSubject, setAddingSubject] = useState(false);
  const [addSubjectError, setAddSubjectError] = useState("");

  const availableCategories = useMemo(
    () =>
      filterCategoriesForSelection(subjectData, selectedBoards, selectedGrades),
    [subjectData, selectedBoards, selectedGrades],
  );

  const subjectGroups = useMemo(
    () => groupCategoriesBySubjectName(availableCategories),
    [availableCategories],
  );

  const filteredSubjectGroups = useMemo(() => {
    const query = subjectSearch.trim().toLowerCase();
    if (!query) return subjectGroups;
    return subjectGroups.filter((group) =>
      group.subjectName.toLowerCase().includes(query),
    );
  }, [subjectGroups, subjectSearch]);

  const toggleInList = (list, value, checked) =>
    checked ? [...list, value] : list.filter((item) => item !== value);

  const handleBoardToggle = (board, checked) => {
    onBoardsChange(toggleInList(selectedBoards, board, checked));
  };

  const handleGradeToggle = (grade, checked) => {
    onGradesChange(toggleInList(selectedGrades, grade, checked));
  };

  const handleSubjectGroupToggle = (categoryIds, checked) => {
    onCategoryIdsChange(
      toggleSubjectGroupSelection(categoryIds, checked, selectedCategoryIds),
    );
  };

  const handleSelectAllSubjects = () => {
    const allIds = filteredSubjectGroups.flatMap((group) => group.categoryIds);
    onCategoryIdsChange(
      toggleSubjectGroupSelection(allIds, true, selectedCategoryIds),
    );
  };

  const handleClearSubjects = () => {
    const visibleIds = new Set(
      filteredSubjectGroups.flatMap((group) => group.categoryIds),
    );
    onCategoryIdsChange(
      selectedCategoryIds.filter((id) => !visibleIds.has(id)),
    );
  };

  const boardsGradesReady =
    selectedBoards.length > 0 && selectedGrades.length > 0;

  const selectedSubjectCount = subjectGroups.filter((group) =>
    isSubjectGroupSelected(group.categoryIds, selectedCategoryIds),
  ).length;

  const mergeCatalogAndSelect = async (result) => {
    const categories = Array.isArray(result?.categories) ? result.categories : [];
    const categoryIds = (
      Array.isArray(result?.categoryIds) ? result.categoryIds : []
    )
      .map((id) => Number(id))
      .filter((id) => !Number.isNaN(id));

    // Prefer a full catalog refresh so all board/grade combos appear immediately.
    try {
      const catalogResponse = await adminAPI.get(
        "/admin/subjectCategory?activeRequired=true",
      );
      if (Array.isArray(catalogResponse.data) && typeof onSubjectCatalogUpdated === "function") {
        onSubjectCatalogUpdated(catalogResponse.data, { replace: true });
      } else if (typeof onSubjectCatalogUpdated === "function") {
        onSubjectCatalogUpdated(categories);
      }
    } catch (refreshError) {
      if (typeof onSubjectCatalogUpdated === "function") {
        onSubjectCatalogUpdated(categories);
      }
    }

    if (categoryIds.length > 0) {
      onCategoryIdsChange(
        toggleSubjectGroupSelection(categoryIds, true, selectedCategoryIds),
      );
    }
  };

  const handleAddMissingSubject = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setAddSubjectError("");

    const subjectName = newSubjectName.trim().replace(/\s+/g, " ");
    if (!boardsGradesReady) {
      setAddSubjectError("Select at least one board and one grade first.");
      return;
    }
    if (subjectName.length < 2) {
      setAddSubjectError("Enter a subject name with at least 2 characters.");
      return;
    }

    setAddingSubject(true);
    try {
      const response = await adminAPI.post(
        "/admin/subjectCategory/tutor-missing",
        {
          subjectName,
          boards: selectedBoards,
          gradeNames: selectedGrades,
          addedByUserId: addedByUserId || undefined,
          addedByTutorId: addedByTutorId || undefined,
          addedByName: addedByName || undefined,
        },
      );
      await mergeCatalogAndSelect(response.data);
      setNewSubjectName("");
      setShowAddSubject(false);
      const name = response.data?.subjectName || subjectName;
      if (response.data?.adminNotified === false) {
        toast.warning(
          `"${name}" was added and selected, but admin notification failed. Click Save on your profile to keep it.`,
        );
      } else {
        toast.success(
          `"${name}" added and selected. Click Save on your profile to keep it. Admin has been notified.`,
        );
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.response?.data?.error ||
        "Could not add subject. Please try again.";
      setAddSubjectError(message);
      toast.error(message);
    } finally {
      setAddingSubject(false);
    }
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.notice}>
        All classes are conducted online.
      </div>

      <section className={styles.section}>
        <h4 className={styles.sectionTitle}>
          Boards you teach <span className={styles.required}>*</span>
        </h4>
        <p className={styles.hint}>Select one or more boards.</p>
        <div className={styles.chipGrid}>
          {BOARD_OPTIONS.map((board) => (
            <label key={board} className={styles.chip}>
              <input
                type="checkbox"
                checked={selectedBoards.includes(board)}
                onChange={(e) => handleBoardToggle(board, e.target.checked)}
              />
              <span>{BOARD_LABELS[board] || board}</span>
            </label>
          ))}
        </div>
        {errors.boards ? (
          <p className={styles.error}>{errors.boards}</p>
        ) : null}
      </section>

      <section className={styles.section}>
        <h4 className={styles.sectionTitle}>
          Grades you teach <span className={styles.required}>*</span>
        </h4>
        <p className={styles.hint}>Select one or more grade levels.</p>
        <div className={styles.chipGrid}>
          {GRADE_OPTIONS.map((grade) => (
            <label key={grade} className={styles.chip}>
              <input
                type="checkbox"
                checked={selectedGrades.includes(grade)}
                onChange={(e) => handleGradeToggle(grade, e.target.checked)}
              />
              <span>{GRADE_LABELS[grade] || grade}</span>
            </label>
          ))}
        </div>
        {errors.grades ? (
          <p className={styles.error}>{errors.grades}</p>
        ) : null}
      </section>

      <section className={styles.section}>
        <div className={styles.subjectHeader}>
          <div>
            <h4 className={styles.sectionTitle}>
              Subjects <span className={styles.required}>*</span>
            </h4>
            <p className={styles.hint}>
              Pick the subjects you teach. Each subject applies to all selected
              boards and grades automatically.
            </p>
          </div>
          {boardsGradesReady && subjectGroups.length > 0 ? (
            <span className={styles.subjectCountBadge}>
              {selectedSubjectCount} of {subjectGroups.length} selected
            </span>
          ) : null}
        </div>

        {!boardsGradesReady ? (
          <p className={styles.placeholder}>
            Select at least one board and one grade above to see subjects.
          </p>
        ) : subjectGroups.length === 0 ? (
          <p className={styles.placeholder}>
            No subjects found for the selected board and grade combination.
          </p>
        ) : (
          <>
            <div className={styles.subjectToolbar}>
              <input
                type="search"
                className={styles.subjectSearch}
                placeholder="Search subjects…"
                value={subjectSearch}
                onChange={(e) => setSubjectSearch(e.target.value)}
                aria-label="Search subjects"
              />
              <div className={styles.subjectActions}>
                <button
                  type="button"
                  className={styles.subjectActionBtn}
                  onClick={handleSelectAllSubjects}
                  disabled={filteredSubjectGroups.length === 0}
                >
                  Select all
                </button>
                <button
                  type="button"
                  className={styles.subjectActionBtn}
                  onClick={handleClearSubjects}
                  disabled={selectedSubjectCount === 0}
                >
                  Clear
                </button>
              </div>
            </div>

            {filteredSubjectGroups.length === 0 ? (
              <p className={styles.placeholder}>
                No subjects match &ldquo;{subjectSearch}&rdquo;.
              </p>
            ) : (
              <div className={styles.subjectChipGrid}>
                {filteredSubjectGroups.map((group) => {
                  const checked = isSubjectGroupSelected(
                    group.categoryIds,
                    selectedCategoryIds,
                  );

                  return (
                    <label
                      key={group.subjectName}
                      className={`${styles.subjectChip} ${
                        checked ? styles.subjectChipSelected : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) =>
                          handleSubjectGroupToggle(
                            group.categoryIds,
                            e.target.checked,
                          )
                        }
                      />
                      <span className={styles.subjectChipLabel}>
                        {group.subjectName}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </>
        )}

        {allowAddMissingSubject && boardsGradesReady ? (
          <div className={styles.addSubjectPanel}>
            {!showAddSubject ? (
              <button
                type="button"
                className={styles.addSubjectToggle}
                onClick={() => {
                  setShowAddSubject(true);
                  setAddSubjectError("");
                }}
              >
                Subject missing? Add it
              </button>
            ) : (
              <div className={styles.addSubjectForm}>
                <p className={styles.addSubjectHint}>
                  Add a missing subject for your selected boards and grades. It
                  becomes available immediately and admin is notified (no
                  approval needed).
                </p>
                <div className={styles.addSubjectRow}>
                  <input
                    type="text"
                    className={styles.addSubjectInput}
                    placeholder="e.g. Environmental Science"
                    value={newSubjectName}
                    onChange={(e) => setNewSubjectName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        e.stopPropagation();
                        if (!addingSubject && newSubjectName.trim()) {
                          handleAddMissingSubject(e);
                        }
                      }
                    }}
                    maxLength={100}
                    disabled={addingSubject}
                    aria-label="New subject name"
                  />
                  <button
                    type="button"
                    className={styles.addSubjectSubmit}
                    disabled={addingSubject || !newSubjectName.trim()}
                    onClick={handleAddMissingSubject}
                  >
                    {addingSubject ? "Adding…" : "Add subject"}
                  </button>
                  <button
                    type="button"
                    className={styles.subjectActionBtn}
                    onClick={() => {
                      setShowAddSubject(false);
                      setNewSubjectName("");
                      setAddSubjectError("");
                    }}
                    disabled={addingSubject}
                  >
                    Cancel
                  </button>
                </div>
                {addSubjectError ? (
                  <p className={styles.error}>{addSubjectError}</p>
                ) : null}
              </div>
            )}
          </div>
        ) : null}

        {errors.subjects ? (
          <p className={styles.error}>{errors.subjects}</p>
        ) : null}
      </section>

      <section className={styles.section}>
        <h4 className={styles.sectionTitle}>
          Session type <span className={styles.required}>*</span>
        </h4>
        <p className={styles.hint}>You can offer one-on-one, group, or both.</p>
        <div className={styles.chipGrid}>
          <label className={styles.chip}>
            <input
              type="checkbox"
              checked={studyGroupTypes.oneOnOne}
              onChange={(e) =>
                onStudyGroupTypesChange({
                  ...studyGroupTypes,
                  oneOnOne: e.target.checked,
                })
              }
            />
            <span>One on one</span>
          </label>
          <label className={styles.chip}>
            <input
              type="checkbox"
              checked={studyGroupTypes.group}
              onChange={(e) =>
                onStudyGroupTypesChange({
                  ...studyGroupTypes,
                  group: e.target.checked,
                })
              }
            />
            <span>Group</span>
          </label>
        </div>
        {errors.studyGroupType ? (
          <p className={styles.error}>{errors.studyGroupType}</p>
        ) : null}
      </section>
    </div>
  );
};

export default TutorTeachingPreferences;
